import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Unauthenticated hook for the marketing homepage's Morning Brief block.
 * Hits GET /v1/public/:citySlug/alerts/latest — CDN-cached, no tenant
 * context, no Keycloak token. Shape matches apps/api/src/routes/public-alerts.ts.
 */

export interface PublicAlert {
  id: string;
  title: string;
  summary: string | null;
  category: string;
  severity: "info" | "watch" | "advisory" | "warning" | "critical";
  level: 1 | 2 | 3 | 4;
  published_at: string;
  source_url: string | null;
  source_name: string | null;
  region_tags: string[];
  /** Published inside the current window (meta.currentWindowDays). */
  is_current?: boolean;
}

export interface PublicAlertsMeta {
  /** Days a notice counts as current. Older APIs omit it. */
  currentWindowDays?: number;
  /** Current notices across the whole feed, not just this page. */
  currentCount?: number;
  sourceCount: number;
  healthyCount: number;
  lastSuccessAt: string | null;
  lastPolledAt: string | null;
  pollIntervalMin: number;
}

export interface PublicAlertsResponse {
  city: { slug: string; name: string; timezone: string };
  items: PublicAlert[];
  meta: PublicAlertsMeta;
}

export interface PublicAlertsFilters {
  category?:
    | "infectious_disease"
    | "immunisation"
    | "medication"
    | "public_health"
    | "recall"
    | "clinical_guideline"
    | "workforce"
    | "other";
}

export function usePublicAlerts(
  citySlug: string,
  limit = 5,
  filters: PublicAlertsFilters = {},
) {
  return useQuery({
    queryKey: ["public-alerts", citySlug, limit, filters],
    queryFn: () =>
      api.get<PublicAlertsResponse>(`/v1/public/${citySlug}/alerts`, {
        skipAuth: true,
        params: { limit, category: filters.category },
      }),
    // Cache longer than the CDN header — landing-page visitors don't need
    // second-by-second freshness.
    staleTime: 2 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export type AlertLevel = 1 | 2 | 3 | 4;

/**
 * The public 4-step scale. ACT Health doesn't grade its notices, so
 * PulseBoard grades each one from its own wording at ingest
 * (apps/rss-ingestor/src/classify.ts) and explains the rules on /alerts.
 */
export const ALERT_LEVELS: Record<AlertLevel, { label: string; meaning: string }> = {
  4: { label: "Urgent", meaning: "A case or exposure in the ACT" },
  3: { label: "Warning", meaning: "A disease risk spreading in Australia, or a product-safety problem" },
  2: { label: "Advisory", meaning: "Overseas outbreaks and clinical information" },
  1: { label: "Update", meaning: "Vaccine programs, eligibility and supply changes" },
};

export const DEFAULT_CURRENT_WINDOW_DAYS = 14;

export function alertLevelFromSeverity(severity: string): AlertLevel {
  if (severity === "critical") return 4;
  if (severity === "warning") return 3;
  if (severity === "advisory" || severity === "watch") return 2;
  return 1;
}

/** Coral only for Urgent, amber for Warning, everything else stays muted. */
export function alertTone(level: AlertLevel): "urgent" | "warn" | "neutral" {
  return level === 4 ? "urgent" : level === 3 ? "warn" : "neutral";
}

/** Falls back to the published date for responses from an older API. */
export function isCurrentAlert(alert: PublicAlert, windowDays = DEFAULT_CURRENT_WINDOW_DAYS): boolean {
  if (typeof alert.is_current === "boolean") return alert.is_current;
  return Date.now() - new Date(alert.published_at).getTime() <= windowDays * 86_400_000;
}

/** "21 Aug 2026", always in Canberra time so every visitor sees the same date. */
export function formatAlertDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Australia/Sydney",
  });
}

/** A short source link label: PDFs say so, because they open a download. */
export function alertSourceLinkLabel(alert: Pick<PublicAlert, "source_name" | "source_url">): string {
  const source = alert.source_name ?? "the official source";
  return /\.pdf(\?|$)/i.test(alert.source_url ?? "") ? `Read the ${source} notice (PDF)` : `Read on ${source}`;
}

export function formatRelativeAlertTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}
